import Modal from "@via/components/modal";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import { ModalSize } from "@via/utils/constants";
import { useEffect, useState } from "react";
import { useEditDatasetFile } from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { VmetaDataResponse, IMetaFieldTypes } from "common";
import {
  processError,
  formatSpreadsheetCellValue,
  getInitialColumnVisibility,
  DEFAULT_HIDDEN_SPREADSHEET_COLUMNS,
} from "@via/pages/data/premium-dataset/utils/helper";
interface EditPremiumDatasetFileProps {
  datasetFiles: {
    data: VmetaDataResponse[];
    meta: {
      field_types: Record<string, unknown>[];
    };
  };
  projectIds: string[];
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const EditPremiumDatasetFile = ({
  datasetFiles,
  projectIds,
  isLoading,
  isOpen,
  onClose,
}: EditPremiumDatasetFileProps) => {
  const { setNotification } = useNotifications();
  const [labels, setLabels] = useState<IMetaFieldTypes[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const { mutateAsync: editDatasetFile, isPending: isEditDatasetFileLoading } =
    useEditDatasetFile();

  useEffect(() => {
    const allLabels =
      datasetFiles.meta?.field_types.filter(
        fieldType => !fieldType.hasNamingPattern
      ) || ([] as IMetaFieldTypes[]);

    const allSpreadsheetData = datasetFiles.data.map(file => {
      return (
        allLabels?.map(label => {
          const value = file[label.name as string];
          return formatSpreadsheetCellValue(value);
        }) || []
      );
    });

    setLabels(allLabels as unknown as IMetaFieldTypes[]);
    setSpreadsheetData(allSpreadsheetData);

    // Auto-calculate visibility based on data
    if (allSpreadsheetData.length > 0) {
      const labelNames = allLabels.map(l => l.label);
      const initialVisibility = getInitialColumnVisibility(
        labelNames as string[],
        allSpreadsheetData
      );
      // make sure or custom user fields are visible on load:
      for (const label in initialVisibility) {
        if (!DEFAULT_HIDDEN_SPREADSHEET_COLUMNS.includes(label)) {
          initialVisibility[label] = true;
        }
      }
      // hide File Layout by default
      initialVisibility["File Layout"] = false;
      setColumnVisibility(initialVisibility);
    }
  }, [datasetFiles]);

  const updateSpreadsheetData = (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    setSpreadsheetData(data);
    const changedRowIndices =
      rowIndices && rowIndices.length > 0
        ? rowIndices
        : rowIndex !== undefined
          ? [rowIndex]
          : [];
    if (changedRowIndices.length === 0) {
      return;
    }
    setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
  };

  const handleConfirm = async () => {
    const transformedDatas: any = [];
    const rowErrors = [];

    try {
      // Only process rows that have been modified
      const changedRowsArray = Array.from(changedRows);

      changedRowsArray.forEach(rowIndex => {
        const row = spreadsheetData[rowIndex];
        const transformedData: any = {};

        row.forEach((cell, colIndex) => {
          const label = labels[colIndex];
          const labelName = label?.label;
          // Only include visible columns
          if (label && labelName && columnVisibility[labelName] !== false) {
            const stringValue = cell.value?.toString() ?? "";
            const value = stringValue === "" ? null : stringValue;
            transformedData[labelName] =
              value != null &&
              typeof value === "string" &&
              (value.startsWith("{") || value.startsWith("["))
                ? JSON.parse(value)
                : value;
          }
        });
        transformedData["canvasId"] = projectIds[rowIndex] || "";
        transformedData["_id"] = datasetFiles.data[rowIndex]?._id || "";
        transformedDatas.push(transformedData);
      });

      const results = await Promise.allSettled(
        transformedDatas.map(async transformedData => {
          return await editDatasetFile(transformedData);
        })
      );

      const errorRowIndices = new Set<number>();

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          processError(result.reason, changedRowsArray[index], rowErrors);
          errorRowIndices.add(changedRowsArray[index]);
        } else if (result.status === "fulfilled" && "error" in result.value) {
          processError(result.value.error, changedRowsArray[index], rowErrors);
          errorRowIndices.add(changedRowsArray[index]);
        }
      });

      if (rowErrors.length) {
        setChangedRows(errorRowIndices);
        setErrors(rowErrors);
      } else {
        setChangedRows(new Set());
        onClose();
      }
    } catch (_error) {
      setNotification({
        id: "error-transforming-data",
        message: "Unable to process file",
        details: "Please ensure the data is in the correct format.",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Edit Dataset File"
      showButtons={false}
      isLoading={isLoading}
      size={ModalSize.Large}
      width={1000}>
      <>
        <MessageBanner messages={errors} type={BannerType.Error} />
        <div className="mb-5">
          <SpreadsheetEditor
            columnLabels={labels.map(label => label?.label)}
            data={spreadsheetData}
            onChange={updateSpreadsheetData}
            isLoading={isEditDatasetFileLoading}
            disableContextMenu
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
          <div className="flex justify-end mt-4">
            <button
              id="update-button"
              type="button"
              disabled={isEditDatasetFileLoading}
              onClick={e => {
                e.preventDefault();
                handleConfirm();
              }}
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Update
            </button>
            <button
              id="cancel-button"
              type="button"
              className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default EditPremiumDatasetFile;
